import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom"; 
import Dialog from "../../../../../../common/element/Dialog";
import Select from "../../../../../../common/element/Select";
import SweetAlertService from '../../../../../../common/SweetAlert/SweetAlert';
import { getSessionMasterList } from "../../../../../../redux/SessionMaster/sessionMasterSlice";
import { selectSessionMasterList } from "../../../../../../redux/SessionMaster/sessionMasterSelector";
import {
    addScienceOfSelfSession,
    clearAddScienceOfSelfSession
} from "../../../../../../redux/ScienceOfSelf/Scienceofselfslice";
import {
    selectAddScienceOfSelfSessionLoading,
    selectAddScienceOfSelfSessionError,
    selectAddScienceOfSelfSessionData
} from "../../../../../../redux/ScienceOfSelf/Scienceofselfselectors";

const AddNewSession = ({
    dialogOpen,
    onDialogClose,
    onSessionAdded,
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const scienceofselfid = searchParams.get('id');
    const selectSessionMasterListData = useSelector(selectSessionMasterList);
    const addSessionLoading = useSelector(selectAddScienceOfSelfSessionLoading);
    const addSessionError = useSelector(selectAddScienceOfSelfSessionError);
    const addSessionData = useSelector(selectAddScienceOfSelfSessionData);

    const [formData, setFormData] = useState({
        sessionName: null,
        date: "",
        time: ""
    });

    const [errors, setErrors] = useState({});

    const sessionOptions = useMemo(() => selectSessionMasterListData?.map(session => ({
        value: session.id || session.session_id,
        label: session.name || session.session_name || session.title
    })) || [], [selectSessionMasterListData]);

    useEffect(() => {
        const payload = {
            page: 0,
            limit: 0,
            archived: false,
            access_group_id: 4
        };
        dispatch(getSessionMasterList(payload));
    }, [dispatch]);

    useEffect(() => {
        if (addSessionData && !addSessionLoading) {
            setTimeout(() => {
                SweetAlertService.showSuccess({
                    title: 'Success!',
                    message: addSessionData?.message || 'Session added successfully',
                    onConfirm: () => {
                        dispatch(clearAddScienceOfSelfSession());
                        if (onSessionAdded) onSessionAdded();
                        onDialogClose();
                    }
                });
            }, 200);
        }
    }, [addSessionData, addSessionLoading, onSessionAdded, onDialogClose, dispatch]);

    useEffect(() => {
        if (addSessionError) {
            SweetAlertService.showError({
                title: 'Error!',
                message: addSessionError || 'Failed to add session',
            });
        }
    }, [addSessionError]);

    useEffect(() => {
        if (!dialogOpen) {
            setFormData({ sessionName: null, date: "", time: "" });
            setErrors({});
            dispatch(clearAddScienceOfSelfSession());
        }
    }, [dialogOpen, dispatch]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const handleSubmit = () => {
        let newErrors = {};

        if (!formData.sessionName) newErrors.sessionName = "Please select a session";
        if (!formData.date) newErrors.date = "Please select a date";
        // if (!formData.time) newErrors.time = "Please select a time";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const payload = {
            science_of_self_id: parseInt(scienceofselfid), 
            session_id: formData.sessionName.value, 
            session_date: formData.date
        };

        dispatch(addScienceOfSelfSession(payload));
    };

    const FormRow = ({ label, required, children }) => (
        <div className="flex items-start mb-6 last:mb-0">
            <label className="w-[30%] pt-2.5 font-lato font-medium text-[14px] leading-[150%] text-[#344054]">
                {label}{required && "*"}
            </label>
            <div className="w-[70%]">
                {children}
            </div>
        </div>
    );

    return (
        <Dialog
            isOpen={dialogOpen}
            onClose={onDialogClose}
            width={800}
            onRequestClose={onDialogClose}
            bodyOpenClassName="overflow-hidden"
        >
            {/* --- HEADER --- */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 border border-gray-200 rounded-lg shadow-sm bg-white">
                        <svg width="20" height="20" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.74771 3.01889C4.58134 3.01889 5.25714 2.34309 5.25714 1.50944C5.25714 0.675801 4.58134 0 3.74771 0C2.91408 0 2.23828 0.675801 2.23828 1.50944C2.23828 2.34309 2.91408 3.01889 3.74771 3.01889Z" fill="#344054" />
                            <path d="M4.90673 14.144H0.00195312V5.78491C0.00195312 5.38801 0.323554 5.06641 0.720455 5.06641C1.11736 5.06641 1.43896 5.38801 1.43896 5.78491V12.707H4.90673C5.30363 12.707 5.62523 13.0286 5.62523 13.4255C5.62523 13.8224 5.30363 14.144 4.90673 14.144Z" fill="#344054" />
                            <path d="M7.15688 17.9867C6.60133 17.9867 6.15098 17.5363 6.15098 16.9807V13.2776L2.96342 12.4889C2.5145 12.378 2.19922 11.975 2.19922 11.5126V4.58648C2.19922 4.03093 2.64958 3.58057 3.20512 3.58057C3.76067 3.58057 4.21102 4.03093 4.21102 4.58648V10.7248L7.39858 11.5135C7.8475 11.6244 8.16278 12.0274 8.16278 12.4898V16.9805C8.16278 17.5363 7.71243 17.9867 7.15688 17.9867Z" fill="#344054" />
                            <path d="M16.2706 3.30681C17.1093 3.30681 17.7892 2.62689 17.7892 1.78817C17.7892 0.949449 17.1093 0.269531 16.2706 0.269531C15.4319 0.269531 14.752 0.949449 14.752 1.78817C14.752 2.62689 15.4319 3.30681 16.2706 3.30681Z" fill="#344054" />
                            <path d="M20.0005 14.2705H15.0955C14.6986 14.2705 14.377 13.9489 14.377 13.552C14.377 13.1551 14.6986 12.8335 15.0955 12.8335H18.5635V5.91138C18.5635 5.51448 18.8851 5.19287 19.282 5.19287C19.6789 5.19287 20.0005 5.51448 20.0005 5.91138V14.2705Z" fill="#344054" />
                            <path d="M20 13.5518H14.9785V17.7907H20V13.5518Z" fill="#344054" />
                        </svg>
                    </div>
                    <h5 className="font-lato font-medium text-[16px] text-[#101828]">
                        Add New Session
                    </h5>
                </div>
                <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
            </div>

            {/* --- FORM BODY --- */}
            <div className="px-6 py-10">
                
                {/* Session Name Dropdown */}
                <FormRow label="Session Name" required>
                    <Select
                        className="w-full h-9 select-options"
                        placeholder="Select Session"
                        options={sessionOptions}
                        value={formData.sessionName}
                        onChange={(opt) => handleInputChange("sessionName", opt)}
                    />
                    {errors.sessionName && <p className="text-xs text-red-600 mt-1">{errors.sessionName}</p>}
                </FormRow>

                {/* Date and Time Fields */}
                <FormRow label="Date of Session" required>
                    <div className="flex gap-4">

                        {/* Date Picker Wrapper */}
                        <div className="flex-1">
                            <div className="relative w-full">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                </div>
                                <input
                                    type="date"
                                    className={`w-full h-[38px] pl-10 pr-3 text-sm input input-shadow rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Select Date"
                                    value={formData.date}
                                    onChange={(e) => handleInputChange("date", e.target.value)}
                                    min={new Date().toISOString().split('T')[0]} /* <--- ADDED MIN ATTRIBUTE HERE */
                                />
                            </div>
                            {errors.date && <p className="text-xs text-red-600 mt-1">{errors.date}</p>}
                        </div>

                        {/* Time Picker Wrapper */}
                        {/* <div className="flex-1">
                            <div className="relative w-full">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                </div>
                                <input
                                    type="time"
                                    className={`w-full h-[38px] pl-10 pr-3 text-sm input input-shadow rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.time ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Select Time"
                                    value={formData.time}
                                    onChange={(e) => handleInputChange("time", e.target.value)}
                                />
                            </div>
                            {errors.time && <p className="text-xs text-red-600 mt-1">{errors.time}</p>}
                        </div> */}

                    </div>
                </FormRow>
            </div>

            {/* --- FOOTER --- */}
            <div className="px-6 py-5 border-t border-gray-100 flex items-center gap-4">
                <button
                    className="flex-1 h-11 bg-[#FFF4E5] text-[#101828] font-medium rounded-lg text-sm hover:bg-orange-100 transition-colors"
                    onClick={onDialogClose}
                    disabled={addSessionLoading}
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={addSessionLoading}
                    className="flex-1 h-11 bg-[#FF8E1C] text-white font-medium rounded-lg text-sm hover:bg-[#e68019] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {addSessionLoading ? "Adding..." : "Add Session"}
                </button>
            </div>
        </Dialog>
    );
};

export default AddNewSession;